import { useEffect, useState } from "react";

// iOS Safari shrinks the VISUAL viewport for the software keyboard but leaves the
// LAYOUT viewport alone. A `position: fixed` dialog centred with translate(-50%,-50%)
// therefore stays centred on the original height and its lower half — fields,
// footer, submit button — ends up behind the keyboard, with body scroll locked by
// Radix so there is nothing to scroll.
//
// `vh` does not help: it does not change when the keyboard opens. `dvh` reacts to
// browser chrome, not reliably to the keyboard either. `window.visualViewport` is
// the only thing that actually knows, so that is what we use, with a dvh cap as
// the fallback for anything without it.
export function useVisualViewportMaxHeight(ratio = 0.92) {
  const [maxHeight, setMaxHeight] = useState(null);

  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;
    const apply = () => setMaxHeight(`${Math.round(vv.height * ratio)}px`);
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
    };
  }, [ratio]);

  return maxHeight ? { maxHeight } : undefined;
}

// iOS scrolls a focused field into view for the page, but not reliably inside a
// fixed, scroll-locked container. Do it ourselves, on the container.
export function useScrollFocusIntoView(ref) {
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onFocus = (e) => {
      const t = e.target;
      if (!t || !t.closest || !t.closest("input, textarea, select, [contenteditable]")) return;
      // Let the keyboard finish opening, otherwise we scroll against the old height.
      setTimeout(() => {
        try { t.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* older engines */ }
      }, 250);
    };
    node.addEventListener("focusin", onFocus);
    return () => node.removeEventListener("focusin", onFocus);
  }, [ref]);
}
